import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpService } from '../../../app/main/services/http.service';
import { APPCONFIG } from '../../../app/main/config';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';

/**
 * Record of every rejected POD attempt for a job (the "POD Retry Logs" dialog on the Job Summary
 * page). View-only for most users - unlike delivery documents, there is no upload or delete here,
 * since these are a log of past AI-scan attempts. Portal Admins additionally get an "Approve"
 * button per attempt, to manually accept a rejected POD as the final one for the job.
 */
@Component({
  selector: 'view-pod-retry-logs-dialog',
  templateUrl: './view-pod-retry-logs-dialog.component.html?v=${APPCONFIG.cacheVersion}',
  styleUrls: ['./view-pod-retry-logs-dialog.component.scss?v=${APPCONFIG.cacheVersion}'],
  providers: [DatePipe]
})
export class ViewPodRetryLogsDialogComponent implements OnInit {
  public title: string = 'POD Retry Logs';
  public jobPkId: number;
  public isPortalAdmin: boolean = false;
  public logs: any[] = [];
  public selectedLog: any;
  public selectedBlobUrl: any;
  public loadFailed: boolean = false;
  public loading: boolean = true;
  /** Whether this job's POD has already been approved (via this dialog or otherwise) - gates the Approve controls. */
  public jobApproved: boolean = false;
  public approvedByName: string = null;
  confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;

  constructor(private spinner: NgxSpinnerService, private cdr: ChangeDetectorRef, private http: HttpClient,
    private http1: HttpService, private domSanitizer: DomSanitizer, public dialog: MatDialog,
    private datePipe: DatePipe,
    public dialogRef: MatDialogRef<ViewPodRetryLogsDialogComponent>
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.fetchLogs();
    this.fetchApprovalStatus();
  }

  close() {
    this.dialogRef.close();
  }

  fetchApprovalStatus() {
    const httpOptions = { headers: new HttpHeaders({'Content-Type':'application/json', 'idToken':JSON.parse(localStorage.getItem('Token'))}) };
    this.http.get(APPCONFIG.appUrl + '/jobs/pod/' + this.jobPkId, httpOptions)
      .subscribe((data: any) => {
        // `carrier` is only set true by the backend when an epw_details row actually exists for
        // this job - without that gate, a job that was AI-rejected and never had any successful
        // submission (no epw_details row at all) has approvalPending default to false too, which
        // would be wrongly read as "already approved".
        if ((data.httpStatusCode == 200 || data.httpStatusCode == 201) && data.data && data.data.carrier && !data.data.approvalPending) {
          this.jobApproved = true;
          this.approvedByName = data.data.approvedBy;
          this.cdr.detectChanges();
        }
      },
      err => {});
  }

  fetchLogs() {
    this.spinner.show();
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json', 'idToken': JSON.parse(localStorage.getItem('Token')) }) };

    this.http.get(APPCONFIG.appUrl + '/epw/podRetryLog/' + this.jobPkId, httpOptions)
      .subscribe((data: any) => {
        if (data.httpStatusCode == 200 || data.httpStatusCode == 201) {
          this.logs = data.data || [];
          if (this.logs.length > 0) {
            this.select(this.logs[0]);
          }
        } else {
          this.loadFailed = true;
        }
        this.loading = false;
        this.spinner.hide();
        this.cdr.detectChanges();
      },
        err => {
          this.loading = false;
          this.spinner.hide();
          this.loadFailed = true;
          let title = "Failed to get the POD retry logs";
          this.http1.errorHandler(err, title);
        });
  }

  select(log: any) {
    this.selectedLog = log;
    if (log.fileSrc) {
      var buffer = this.base64ToArrayBuffer(log.fileSrc);
      var mimeType = (log.fileType == 'pdf') ? 'application/pdf' : 'image/jpg';
      var blob = new Blob([buffer], { type: mimeType });
      this.selectedBlobUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
    } else {
      this.selectedBlobUrl = null;
    }
    this.cdr.detectChanges();
  }

  initApprovePod(log: any) {
    this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
      disableClose: false
    });
    this.confirmDialogRef.componentInstance.confirmMessage = 'Are you sure you want to approve this POD for the job?';
    this.confirmDialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.approvePod(log);
      }
    });
  }

  approvePod(log: any) {
    this.spinner.show();
    const podDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    const body = JSON.stringify({
      'jobPkId': this.jobPkId,
      'deliveryPodDetails': [
        { 'podSrc': log.fileSrc, 'podFileType': log.fileType, 'podSrcType': 'uploaded', 'deliveryNumber': log.deliveryNumber }
      ],
      'podDate': podDate,
      'worksmanName': log.worksmanName,
      'podRetryLogId': log.id,
      'byPassAISCan': true
    });
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json', 'idToken': JSON.parse(localStorage.getItem('Token')) }) };

    this.http.post(APPCONFIG.appUrl + '/epw/approveAIScanFailedPodForAdmin', body, httpOptions)
      .subscribe((data: any) => {
        this.spinner.hide();
        if (data.httpStatusCode == 200 || data.httpStatusCode == 201) {
          this.jobApproved = true;
          this.approvedByName = log.worksmanName;
          this.dialogRef.close(true);
        } else {
          let title = "Failed to approve the POD";
          this.http1.errorHandler(data, title);
        }
      },
        err => {
          this.spinner.hide();
          let title = "Failed to approve the POD";
          this.http1.errorHandler(err, title);
        });
  }

  base64ToArrayBuffer(base64) {
    var binary_string = window.atob(base64.replace(/\s/g, ''));
    var len = binary_string.length;
    var bytes = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
